"use client";

import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Undo, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandGroup,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import Avatar from "@/components/avatar";

export interface SelectOption {
  value: string;
  label: string;
  disable?: boolean;
  avatarUrl?: string;
  brand_ids?: string[];
  badge?: string;
}

interface SelectProps {
  options: SelectOption[];
  selected: SelectOption | SelectOption[] | null;
  onChange: (option: SelectOption | SelectOption[] | null) => void;
  placeholder?: string;
  emptyMessage?: string;
  label?: string;
  showAvatar?: boolean;
  hideBadgeAvatars?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  className?: string;
  showUndo?: boolean;
  onUndo?: () => void;
  onClear?: () => void;
}

export function Select({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  emptyMessage = "No options found.",
  label,
  showAvatar = false,
  hideBadgeAvatars = false,
  isMulti = false,
  disabled = false,
  isClearable = false,
  className,
  showUndo = false,
  onUndo,
  onClear,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useIsMobile();
  const showSearch = options.length > 6;

  const selectedOptions = isMulti ? (selected as SelectOption[]) || [] : [];
  const selectedOption = isMulti ? null : (selected as SelectOption | null);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (option: SelectOption) => {
    if (isMulti) {
      const isSelected = selectedOptions.find(
        (item) => item.value === option.value
      );
      if (isSelected) {
        onChange(selectedOptions.filter((item) => item.value !== option.value));
      } else {
        onChange([...selectedOptions, option]);
      }
    } else {
      onChange(option);
      setOpen(false);
    }
    if (!isMulti) {
      setSearchQuery("");
    }
  };

  const handleRemove = (option: SelectOption) => {
    if (isMulti) {
      onChange(selectedOptions.filter((item) => item.value !== option.value));
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(isMulti ? [] : null);
    if (onClear) {
      onClear();
    }
    setOpen(false);
  };

  const commandContent = (
    <Command>
      {showSearch && (
        <CommandInput
          placeholder='Search...'
          value={searchQuery}
          onValueChange={setSearchQuery}
          className={cn(isMobile && "text-base")}
        />
      )}
      <CommandList>
        {filteredOptions.length === 0 && (
          <CommandEmpty>{emptyMessage}</CommandEmpty>
        )}
        <CommandGroup>
          {filteredOptions.map((option) => {
            const isSelected = isMulti
              ? selectedOptions.find((item) => item.value === option.value)
              : selectedOption?.value === option.value;
            return (
              <CommandItem
                key={option.value}
                value={option.label}
                disabled={option.disable}
                onSelect={() => handleSelect(option)}
                className='flex items-center border-b border-muted/40 md:gap-4 py-4 lg:py-2'
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    isSelected ? "opacity-100" : "opacity-0"
                  )}
                />
                {showAvatar && option.avatarUrl && (
                  <Avatar
                    variant='static'
                    size='xs'
                    src={option.avatarUrl}
                    className='flex-shrink-0'
                  />
                )}
                <span className='truncate'>{option.label}</span>
                {option.badge && (
                  <Badge variant='outline' className='ml-2'>
                    {option.badge}
                  </Badge>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  const triggerButton = (
    <Button
      variant='outline'
      role='combobox'
      aria-expanded={open}
      disabled={disabled}
      className={cn(
        "w-full justify-between bg-background font-normal",
        isMulti && "h-auto min-h-10 py-2",
        className
      )}
    >
      <div className={cn("flex", isMulti && "flex-wrap gap-1")}>
        {isMulti ? (
          <>
            {selectedOptions.length === 0 && (
              <span className='text-muted-foreground'>{placeholder}</span>
            )}
            {selectedOptions.map((option) => (
              <Badge
                key={option.value}
                variant='secondary'
                className='mr-1 flex items-center gap-1 rounded-full'
              >
                {showAvatar && !hideBadgeAvatars && option.avatarUrl && (
                  <Avatar
                    src={option.avatarUrl}
                    size='xs'
                    className='h-4 w-4'
                  />
                )}
                {option.label}
                <span
                  role='button'
                  tabIndex={0}
                  className='ml-1 cursor-pointer ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(option);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemove(option);
                  }}
                >
                  <X className='h-3 w-3' />
                </span>
              </Badge>
            ))}
          </>
        ) : (
          <div className='flex items-center gap-2'>
            {showAvatar && selectedOption?.avatarUrl && (
              <Avatar
                variant='static'
                size='xs'
                src={selectedOption.avatarUrl}
                className='flex-shrink-0'
              />
            )}
            {selectedOption ? (
              <div className='flex items-center gap-2'>
                <span className='truncate'>{selectedOption.label}</span>
                {selectedOption.badge && (
                  <Badge variant='outline' className='ml-2'>
                    {selectedOption.badge}
                  </Badge>
                )}
              </div>
            ) : (
              <span className='text-muted-foreground truncate'>
                {placeholder}
              </span>
            )}
          </div>
        )}
      </div>
      <div className='flex items-center gap-2'>
        {showUndo && onUndo && (
          <div
            role='button'
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onUndo();
            }}
            className='rounded-full p-0.5 hover:bg-accent cursor-pointer'
          >
            <Undo className='h-4 w-4 opacity-50' />
          </div>
        )}
        {isClearable && selected && (
          <div
            role='button'
            tabIndex={0}
            onClick={handleClear}
            className='rounded-full p-0.5 hover:bg-accent cursor-pointer'
          >
            <X className='h-4 w-4 opacity-50' />
          </div>
        )}
        <ChevronDown className='h-4 w-4 opacity-50' />
      </div>
    </Button>
  );

  return (
    <div className='space-y-2'>
      {label && <Label className='ml-1'>{label}</Label>}
      {isMobile ? (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild disabled={disabled}>
            {triggerButton}
          </DrawerTrigger>
          <DrawerContent className='h-fit max-h-[60vh] z-[101] pb-4'>
            <div className='mt-4'>
              <h3 className='font-semibold text-2xl text-center mb-4'>
                {label || ""}
              </h3>
              {commandContent}
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild disabled={disabled}>
            {triggerButton}
          </PopoverTrigger>
          <PopoverContent className='w-full min-w-[var(--radix-popper-anchor-width)] p-0'>
            {commandContent}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
